import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Collapse } from "react-bootstrap";
import { io } from "socket.io-client";
import { ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis } from "recharts";
import { toast } from "react-toastify";
import { BsChatDots, BsClock, BsPaperclip } from "react-icons/bs";
import { MdContentCopy, MdDone, MdDoneAll, MdErrorOutline, MdKeyboardArrowDown, MdAddCircle, MdSearch, MdRefresh } from "react-icons/md";
import {
  Utils,
  fetchWithAuth,
  getLocalStorage,
  can,
  copyToClipboard,
  waMeUrl,
  formatWhatsAppMarkdownHtml,
  getWhatsAppQualityDisplay,
  WHATSAPP_USAGE_TYPE_CODES,
  WHATSAPP_MESSAGE_STATUS,
  WHATSAPP_CAMPAIGN_STATUS,
  findOptionByValue,
  filtersObjectHasAnyValue,
  formatDateTime,
  getDefaultPageSize,
  getTotalPages,
  selectStyles,
  templateHeaderFormat,
  isFileHeaderFormat,
  headerFileAccept,
} from "../../utils";
import DateRangePicker from "../common/DateRangePicker/DateRangePicker";
import Select from "react-select";
import { useActionDropdown } from "../../hooks/useActionDropdown";
import { useTableMaxHeight } from "../../hooks/useTableMaxHeight";
import { useScrollEndDetection } from "../../hooks/useScrollEndDetection";
import Modal from "../common/Modal";
import ListPaginationBar from "../common/ListPaginationBar";

function templateButtons(row) {
  const comps = row && Array.isArray(row.components) ? row.components : [];
  const block = comps.find((c) => String(c.type || "").toUpperCase() === "BUTTONS");
  return Array.isArray(block?.buttons) ? block.buttons : [];
}

function templateHasDynamicUrlButton(row) {
  return templateButtons(row).some(
    (b) =>
      String(b?.type || "").toUpperCase() === "URL" && /\{\{\d+\}\}/.test(String(b.url || ""))
  );
}

function templateHasCopyCodeButton(row) {
  return templateButtons(row).some((b) => String(b?.type || "").toUpperCase() === "COPY_CODE");
}

/** Resolve image/video/document/location for a stored inbox message (columns or template header). */
function messageMediaInfo(m) {
  const comps = m && Array.isArray(m.templateComponents) ? m.templateComponents : [];
  const header = comps.find((c) => String(c?.type || "").toLowerCase() === "header");
  const param = header && Array.isArray(header.parameters) ? header.parameters[0] : null;
  let kind = "";
  let id = m?.mediaId != null ? String(m.mediaId).trim() : "";
  let url = m?.mediaUrl != null ? String(m.mediaUrl).trim() : "";
  let location = null;
  if (param && typeof param === "object") {
    kind = String(param.type || "").toLowerCase();
    if (kind === "location" && param.location && typeof param.location === "object") {
      location = {
        latitude: param.location.latitude != null ? String(param.location.latitude).trim() : "",
        longitude: param.location.longitude != null ? String(param.location.longitude).trim() : "",
        name: param.location.name != null ? String(param.location.name).trim() : "",
        address: param.location.address != null ? String(param.location.address).trim() : "",
      };
      if (location.latitude && location.longitude) {
        return { kind: "location", id: "", url: "", location };
      }
    }
    const obj = param[kind] && typeof param[kind] === "object" ? param[kind] : null;
    if (obj) {
      if (!id && obj.id) id = String(obj.id).trim();
      if (!url && obj.link) url = String(obj.link).trim();
    }
  }
  if (!kind) {
    const mime = String(m?.mimeType || "").toLowerCase();
    if (mime.startsWith("image/") || m?.messageType === "image") kind = "image";
    else if (mime.startsWith("video/") || m?.messageType === "video") kind = "video";
    else if (mime.includes("pdf") || m?.messageType === "document") kind = "document";
    else if (id || url) kind = "image";
  }
  if (kind === "text") return null;
  if (!id && !url) return null;
  return { kind: kind || "image", id: id || "", url: url || "", location: null };
}

function WaMessageMedia({ message, appId, phoneNumberId }) {
  const info = useMemo(
    () => messageMediaInfo(message),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recompute when media fields change
    [message?.id, message?.mediaId, message?.mediaUrl, message?.mimeType, message?.messageType, message?.templateComponents]
  );
  const [blobUrl, setBlobUrl] = useState("");
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let objectUrl = "";
    let cancelled = false;
    setLoadFailed(false);
    setBlobUrl("");
    if (!info || info.kind === "location" || info.url || !info.id || !appId) return undefined;
    fetchWithAuth(Utils.baseurl + "application/whatsapp/media/content", {
      method: "POST",
      body: JSON.stringify({
        app_id: appId,
        media_id: info.id,
        ...(phoneNumberId ? { phone_number_id: phoneNumberId } : {}),
      }),
    })
      .then(async (res) => {
        if (!res.ok) throw new Error("media fetch failed");
        const buf = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(buf);
        setBlobUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [info, appId, phoneNumberId]);

  if (!info) return null;
  if (info.kind === "location" && info.location) {
    const { latitude, longitude, name, address } = info.location;
    const mapsUrl = `https://www.google.com/maps?q=${encodeURIComponent(`${latitude},${longitude}`)}`;
    return (
      <div className="wa-bubble__media wa-bubble__media--location">
        <a href={mapsUrl} target="_blank" rel="noreferrer" className="wa-bubble__location-card">
          <div className="wa-bubble__location-pin" aria-hidden>
            📍
          </div>
          <div className="wa-bubble__location-body">
            <div className="wa-bubble__location-title">{name || "Location"}</div>
            {address ? <div className="wa-bubble__location-sub">{address}</div> : null}
            <div className="wa-bubble__location-coords">
              {latitude}, {longitude}
            </div>
          </div>
        </a>
      </div>
    );
  }
  const src = info.url || blobUrl;
  if (info.kind === "image") {
    if (!src) {
      return (
        <div className="wa-bubble__media wa-bubble__media--pending">
          {loadFailed ? "Image unavailable" : "Loading image…"}
        </div>
      );
    }
    return (
      <div className="wa-bubble__media wa-bubble__media--image">
        <img src={src} alt="" />
      </div>
    );
  }
  if (info.kind === "video") {
    if (!src) {
      return (
        <div className="wa-bubble__media wa-bubble__media--pending">
          {loadFailed ? "Video unavailable" : "Loading video…"}
        </div>
      );
    }
    return (
      <div className="wa-bubble__media wa-bubble__media--video">
        <video src={src} controls playsInline preload="metadata" />
      </div>
    );
  }
  return (
    <div className="wa-bubble__media wa-bubble__media--doc">
      {src ? (
        <a href={src} target="_blank" rel="noreferrer">
          <BsPaperclip aria-hidden="true" /> Document
        </a>
      ) : (
        <span className="wa-muted">
          <BsPaperclip aria-hidden="true" /> {loadFailed ? "Document unavailable" : "Document"}
        </span>
      )}
    </div>
  );
}

/**
 * WhatsApp Business Platform UI — Inbox, Contacts, Campaigns, Analytics, Phones.
 * Requires backend migration `whatsapp-migrations-v2-inbox.sql` and Socket.IO on API server.
 */

function renderWhatsAppMarkdown(text) {
  const html = formatWhatsAppMarkdownHtml(text);
  if (!html) return null;
  return <span className="wa-md" dangerouslySetInnerHTML={{ __html: html }} />;
}

function MessageLink({ displayPhone }) {
  const url = waMeUrl(displayPhone);
  if (!url) return null;
  return (
    <span className="wa-msg-link">
      <button
        type="button"
        className="wa-msg-link__copy"
        title="Copy WhatsApp message link"
        aria-label="Copy WhatsApp message link"
        onClick={async () => {
          await copyToClipboard(url);
          toast.success("Copied");
        }}
      >
        <MdContentCopy size={18} aria-hidden="true" />
      </button>
    </span>
  );
}

const WhatsAppPlatform = ({ section, appId }) => {
  const navigate = useNavigate();
  const socketRef = useRef(null);
  const api = useCallback(
    (path, body) =>
      fetchWithAuth(Utils.baseurl + path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ app_id: appId, ...body }),
      }),
    [appId]
  );

  const subscribeWabaWebhooks = useCallback(() => {
    api("application/whatsapp/business/subscribe", {})
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        if (result.status) toast.success("Subscribe request sent");
        else toast.error(result.message || "Error");
      })
      .catch(() => {});
  }, [api]);

  /** Inbox chats for the number assigned Chat role on Phone numbers. */
  const [inboxDisplayPhone, setInboxDisplayPhone] = useState("");
  const [inboxPhoneId, setInboxPhoneId] = useState("");
  const [inboxPhoneReady, setInboxPhoneReady] = useState(false);

  useEffect(() => {
    if (section !== "inbox") return;
    setInboxPhoneReady(false);
    api("application/whatsapp/business/phones", {})
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        if (!result.status) {
          setInboxPhoneId("");
          setInboxDisplayPhone("");
          setInboxPhoneReady(true);
          return;
        }
        const id = result.phone_number_id ? String(result.phone_number_id) : "";
        setInboxPhoneId(id);
        setInboxDisplayPhone(id ? result.display_phone_number || "" : "");
        setInboxPhoneReady(true);
      })
      .catch(() => {
        setInboxPhoneId("");
        setInboxDisplayPhone("");
        setInboxPhoneReady(true);
      });
  }, [api, section]);

  useEffect(() => {
    const token = getLocalStorage(Utils.storageKeys.token);
    if (!token || !appId) return;
    try {
      const base = String(Utils.baseurl || "").replace(/\/$/, "");
      const s = io(base, {
        // Polling first: works through any proxy; upgrades to websocket when the
        // proxy supports it (Apache needs proxy_wstunnel for the upgrade).
        transports: ["polling", "websocket"],
        auth: { token },
      });
      socketRef.current = s;
      s.on("connect", () => {
        s.emit("join", { appId });
      });
      s.on("whatsapp:event", (payload) => {
        window.dispatchEvent(new CustomEvent("whatsapp:refresh", { detail: payload || {} }));
      });
    } catch (_) {}
    return () => {
      try {
        socketRef.current?.disconnect();
      } catch (_) {}
    };
  }, [appId]);

  const isListSection =
    section === "contacts" || section === "campaigns" || section === "phones";
  const pageTitle =
    section === "inbox"
      ? "Inbox"
      : section === "contacts"
        ? "Contacts"
        : section === "campaigns"
          ? "Campaigns"
          : section === "analytics"
            ? "Analytics"
            : "Phone numbers";

  if (isListSection) {
    return (
      <div className="dashboard_right">
        <div className="dashboard_inner">
          <div className="dashboard_right_top">
            <div className="dashboard_heading">
              <h1>{pageTitle}</h1>
            </div>
            {section === "campaigns" && can("whatsapp.send.view") ? (
              <div className="add_app">
                <button
                  type="button"
                  className="add_app_btn btn_theme_primary"
                  onClick={() => navigate(`/apps/${appId}/whatsapp/bulk`)}
                >
                  <MdAddCircle />
                  <span>Send Message</span>
                </button>
              </div>
            ) : null}
            {section === "phones" ? (
              <div className="add_app">
                <button
                  type="button"
                  className="add_app_btn_secondary btn_theme_secondary"
                  onClick={subscribeWabaWebhooks}
                >
                  Subscribe app to WABA webhooks
                </button>
              </div>
            ) : null}
          </div>
          {section === "contacts" ? <ContactsPanel api={api} appId={appId} /> : null}
          {section === "campaigns" ? <CampaignsPanel api={api} appId={appId} /> : null}
          {section === "phones" ? <PhonesPanel api={api} /> : null}
        </div>
      </div>
    );
  }

  return (
    <div className={`wa-platform ${section === "inbox" ? "wa-platform--full" : ""}`}>
      <header className="wa-platform__header">
        <div className="wa-platform__title-row">
          <h1 className="wa-platform__title">{pageTitle}</h1>
          {section === "inbox" && inboxDisplayPhone ? (
            <div className="wa-platform__phone-tools">
              <span className="wa-platform__active-phone" title="Active Chat (Inbox) number">
                {inboxDisplayPhone}
              </span>
              <MessageLink displayPhone={inboxDisplayPhone} />
            </div>
          ) : null}
        </div>
      </header>

      {section === "inbox" && inboxPhoneReady && inboxPhoneId ? (
        <InboxPanel api={api} appId={appId} phoneNumberId={inboxPhoneId} />
      ) : null}
      {section === "inbox" && inboxPhoneReady && !inboxPhoneId ? (
        <div className="wa-muted p-3">
          Assign a Chat number on WhatsApp → Phone numbers to open Inbox.
        </div>
      ) : null}
      {section === "inbox" && !inboxPhoneReady ? (
        <div className="wa-muted p-3">Loading inbox…</div>
      ) : null}
      {section === "analytics" && <AnalyticsPanel api={api} />}
    </div>
  );
};

function InboxPanel({ api, appId, phoneNumberId }) {
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [convDetail, setConvDetail] = useState(null);
  const [text, setText] = useState("");
  const [tplKey, setTplKey] = useState("");
  const [tplBodyParams, setTplBodyParams] = useState("");
  const [tplButtonParam, setTplButtonParam] = useState("");
  const [tplCopyCodeParam, setTplCopyCodeParam] = useState("");
  const [tplHeaderImageUrl, setTplHeaderImageUrl] = useState("");
  const [tplHeaderImageFile, setTplHeaderImageFile] = useState(null);
  const [tplHeaderLat, setTplHeaderLat] = useState("");
  const [tplHeaderLng, setTplHeaderLng] = useState("");
  const [approvedTemplates, setApprovedTemplates] = useState([]);
  const [sending, setSending] = useState(false);

  const TPL_SEP = "\x1f";
  const tplOptionKey = (row) => `${row.name}${TPL_SEP}${row.language || ""}`;

  const selectedTpl = useMemo(() => {
    if (!tplKey) return null;
    return approvedTemplates.find((t) => tplOptionKey(t) === tplKey) || null;
  }, [tplKey, approvedTemplates]);

  const templateBodyText = (row) => {
    const comps = row && Array.isArray(row.components) ? row.components : [];
    const body = comps.find((c) => String(c.type || "").toUpperCase() === "BODY");
    return body?.text != null ? String(body.text) : "";
  };

  const fillPlaceholders = (raw, paramsStr) => {
    let out = String(raw || "");
    const vals = String(paramsStr || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    vals.forEach((v, i) => {
      out = out.split(`{{${i + 1}}}`).join(v);
    });
    return out;
  };

  const selectedTplPreview = useMemo(() => {
    if (!selectedTpl) return "";
    return fillPlaceholders(templateBodyText(selectedTpl), tplBodyParams);
  }, [selectedTpl, tplBodyParams]);

  const selectedTplVarCount = useMemo(() => {
    const s = selectedTpl ? templateBodyText(selectedTpl) : "";
    const re = /\{\{(\d+)\}\}/g;
    let max = 0;
    let m;
    while ((m = re.exec(s)) !== null) {
      const n = parseInt(m[1], 10);
      if (n > max) max = n;
    }
    return max;
  }, [selectedTpl]);

  const tplBodyByName = useMemo(() => {
    const map = new Map();
    for (const t of approvedTemplates) {
      const text = templateBodyText(t);
      if (!text) continue;
      map.set(`${t.name}|${t.language || ""}`, text);
      if (!map.has(t.name)) map.set(t.name, text);
    }
    return map;
  }, [approvedTemplates]);

  /** Bubble / list text: prefer stored body; for old rows that only saved template name, resolve Meta BODY. */
  const displayMessageText = (m) => {
    const raw = (m.body || m.caption || "").trim();
    const name = (m.templateName || "").trim();
    if (m.messageType === "template" || name) {
      const looksLikeNameOnly = raw && name && raw === name;
      const emptyBody = !raw;
      if (looksLikeNameOnly || emptyBody) {
        const fromMeta =
          tplBodyByName.get(`${name}|${m.templateLanguage || ""}`) ||
          tplBodyByName.get(name) ||
          "";
        if (fromMeta) return fromMeta;
      }
    }
    return raw || name || "";
  };

  const loadConvos = useCallback(() => {
    if (!phoneNumberId) {
      setConversations([]);
      return;
    }
    api("application/whatsapp/inbox/conversations", { phone_number_id: phoneNumberId })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        if (result.status) setConversations(result.data || []);
      })
      .catch(() => {});
  }, [api, phoneNumberId]);

  const loadMessages = useCallback(
    (conversationId) => {
      if (!conversationId) return;
      api("application/whatsapp/inbox/messages", { conversation_id: conversationId })
        .then(async (res) => {
          const result = await res.json().catch(() => ({}));
          if (result.status && result.data) {
            setMessages(result.data.messages || []);
            setConvDetail(result.data.conversation);
            api("application/whatsapp/inbox/read", { conversation_id: conversationId }).then(() =>
              loadConvos()
            );
          }
        })
        .catch(() => {});
    },
    [api, loadConvos]
  );

  const loadTemplates = useCallback(() => {
    api("application/whatsapp/templates/list", { limit: 100 })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        if (!result.status) return;
        const rows = Array.isArray(result.data?.data) ? result.data.data : [];
        setApprovedTemplates(
          rows.filter((t) => String(t.status || "").toUpperCase() === "APPROVED")
        );
      })
      .catch(() => setApprovedTemplates([]));
  }, [api]);

  useEffect(() => {
    setActiveId(null);
    setMessages([]);
    setConvDetail(null);
    loadConvos();
    loadTemplates();
  }, [loadConvos, loadTemplates, phoneNumberId]);

  useEffect(() => {
    const onRefresh = (e) => {
      loadConvos();
      const cid = e?.detail?.conversationId;
      if (activeId && (!cid || String(cid) === String(activeId))) {
        loadMessages(activeId);
      }
    };
    window.addEventListener("whatsapp:refresh", onRefresh);
    return () => window.removeEventListener("whatsapp:refresh", onRefresh);
  }, [loadConvos, loadMessages, activeId]);

  useEffect(() => {
    if (activeId) loadMessages(activeId);
  }, [activeId, loadMessages]);

  useEffect(() => {
    if (!activeId && conversations.length > 0) {
      setActiveId(conversations[0].id);
    }
  }, [conversations, activeId]);

  const endRef = useRef(null);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  const send = async () => {
    if (!activeId) return;
    setSending(true);
    try {
      const body = { conversation_id: activeId, phone_number_id: phoneNumberId };
      const useTemplate = Boolean(selectedTpl);

      if (useTemplate) {
        if (selectedTplVarCount > 0) {
          const filled = String(tplBodyParams || "")
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean).length;
          if (filled !== selectedTplVarCount) {
            toast.error(`This template needs ${selectedTplVarCount} variable value(s)`);
            setSending(false);
            return;
          }
        }
        body.templateName = selectedTpl.name;
        body.languageCode = selectedTpl.language || "en_US";
        const tplCat =
          selectedTpl.category != null ? String(selectedTpl.category).trim().toUpperCase() : "";
        if (tplCat) body.template_category = tplCat;
        if (tplBodyParams.trim()) body.body_parameters = tplBodyParams.trim();
        if (tplButtonParam.trim()) body.button_url_param = tplButtonParam.trim();
        if (tplCopyCodeParam.trim()) body.button_copy_code = tplCopyCodeParam.trim();
        if (selectedTplPreview) body.template_preview = selectedTplPreview;
        const hdrFmt = templateHeaderFormat(selectedTpl);
        if (isFileHeaderFormat(hdrFmt)) {
          if (!tplHeaderImageFile && !tplHeaderImageUrl.trim()) {
            toast.error("This template needs header media — upload a file or paste a public HTTPS URL");
            setSending(false);
            return;
          }
          if (tplHeaderImageFile) {
            const fd = new FormData();
            fd.append("app_id", appId);
            fd.append("file", tplHeaderImageFile);
            // Inbox always sends from Chat — upload media to the same phone.
            if (phoneNumberId) fd.append("phone_number_id", phoneNumberId);
            const upRes = await fetchWithAuth(Utils.baseurl + "application/whatsapp/media/upload", {
              method: "POST",
              body: fd,
            });
            const up = await upRes.json().catch(() => ({}));
            if (!up.status || !up.data?.id) {
              toast.error(up.message || "Header media upload failed");
              setSending(false);
              return;
            }
            body.header_image_id = String(up.data.id);
          } else {
            body.header_image_url = tplHeaderImageUrl.trim();
          }
        } else if (hdrFmt === "LOCATION") {
          if (!tplHeaderLat.trim() || !tplHeaderLng.trim()) {
            toast.error("This template needs latitude and longitude for the location header");
            setSending(false);
            return;
          }
          body.header_latitude = tplHeaderLat.trim();
          body.header_longitude = tplHeaderLng.trim();
        }
      } else {
        if (!convDetail?.sessionOpen) {
          toast.info(
            "Custom 1:1 text only works for 24h after the customer messages you. Select a template, or wait for their reply."
          );
          setSending(false);
          return;
        }
        if (!text.trim()) {
          toast.error("Type a message");
          setSending(false);
          return;
        }
        body.text = text.trim();
      }

      const res = await api("application/whatsapp/inbox/send", body);
      const result = await res.json().catch(() => ({}));
      if (!result.status) {
        toast.error(result.message || result.hint || "Send failed");
        setSending(false);
        return;
      }
      if (useTemplate) {
        toast.success("Template sent");
        setTplKey("");
        setTplHeaderImageFile(null);
        setTplHeaderImageUrl("");
        setTplHeaderLat("");
        setTplHeaderLng("");
      }
      setText("");
      setTplBodyParams("");
      loadMessages(activeId);
      loadConvos();
    } finally {
      setSending(false);
    }
  };

  const sendCustom = () => send();

  const fmtTime = (ms) => {
    if (!ms) return "—";
    const m = Math.floor(ms / 60000);
    const h = Math.floor(m / 60);
    if (h > 0) return `${h}h ${m % 60}m`;
    return `${m}m`;
  };

  const initialOf = (c) => {
    const s = String(c?.contactName || c?.contact_phone || c?.contactPhone || "?").trim();
    return s.charAt(0).toUpperCase();
  };

  const convName = (c) => c?.contactName || c?.contact_phone || c?.contactPhone || "Unknown";

  const fmtClock = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return "";
    const today = new Date();
    if (dt.toDateString() === today.toDateString()) {
      return dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
    return dt.toLocaleDateString([], { day: "2-digit", month: "short" });
  };

  const ticks = (m) => {
    if (m.direction !== "outbound") return null;
    const st = Number(m.status);
    if (st === WHATSAPP_MESSAGE_STATUS[3].value) {
      return (
        <span className="wa-tick wa-tick--read" title="Read">
          <MdDoneAll size={14} aria-hidden="true" />
        </span>
      );
    }
    if (st === WHATSAPP_MESSAGE_STATUS[2].value) {
      return (
        <span className="wa-tick" title="Delivered">
          <MdDoneAll size={14} aria-hidden="true" />
        </span>
      );
    }
    if (st === WHATSAPP_MESSAGE_STATUS[1].value) {
      return (
        <span className="wa-tick" title="Sent">
          <MdDone size={14} aria-hidden="true" />
        </span>
      );
    }
    if (st === WHATSAPP_MESSAGE_STATUS[4].value) {
      return (
        <span className="wa-tick wa-tick--fail" title="Failed">
          <MdErrorOutline size={14} aria-hidden="true" />
        </span>
      );
    }
    return (
      <span className="wa-tick" title="Pending">
        <BsClock size={12} aria-hidden="true" />
      </span>
    );
  };

  return (
    <div className="wa-inbox">
      <div className="wa-inbox__list card shadow-sm">
        <div className="wa-inbox__list-head">
          Chats
          <span className="wa-muted" style={{ fontWeight: 400, marginLeft: 6 }}>
            {conversations.length ? `(${conversations.length})` : ""}
          </span>
        </div>
        <div className="wa-inbox__list-body">
          {conversations.length === 0 ? (
            <div className="wa-inbox__list-empty">
              <BsChatDots className="wa-inbox__empty-icon" aria-hidden="true" />
              <div className="wa-muted mt-2">
                No conversations yet.
                <br />
                When a customer messages your WhatsApp number, the chat appears here.
              </div>
            </div>
          ) : (
            conversations.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`wa-inbox__row ${activeId === c.id ? "active" : ""}`}
                onClick={() => setActiveId(c.id)}
              >
                <div className="wa-avatar">{initialOf(c)}</div>
                <div className="wa-inbox__row-main">
                  <div className="wa-inbox__row-top">
                    <span className="wa-inbox__phone">{convName(c)}</span>
                    <span className="wa-inbox__time">{fmtClock(c.lastMessageAt)}</span>
                  </div>
                  <div className="wa-inbox__row-bottom">
                    <span className="wa-inbox__preview">
                      {(() => {
                        const p = c.lastMessagePreview || "";
                        if (p.startsWith("Template: ")) {
                          const name = p.slice("Template: ".length).trim();
                          return renderWhatsAppMarkdown(tplBodyByName.get(name) || p);
                        }
                        return renderWhatsAppMarkdown(p || "—");
                      })()}
                    </span>
                    {c.unreadCount > 0 ? <span className="wa-badge">{c.unreadCount}</span> : null}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>
      <div className="wa-inbox__thread card shadow-sm">
        {!activeId ? (
          <div className="wa-inbox__empty">
            <BsChatDots className="wa-inbox__empty-icon" aria-hidden="true" />
            <div className="wa-inbox__empty-title">notifySphere WhatsApp Inbox</div>
            <div className="wa-muted">
              Customer chats land here in real time. Reply within the 24-hour window, or send an
              approved template any time.
            </div>
          </div>
        ) : (
          <>
            <div className="wa-inbox__thread-head">
              <div className="wa-avatar wa-avatar--lg">{initialOf(convDetail)}</div>
              <div className="wa-inbox__thread-title">
                <strong>{convName(convDetail)}</strong>
                <div className="wa-muted small">
                  {convDetail?.contact_phone || convDetail?.contactPhone || ""}
                </div>
              </div>
              <div
                className={`wa-session-pill ${convDetail?.sessionOpen ? "wa-session-pill--open" : "wa-session-pill--closed"}`}
                title="WhatsApp allows free-form replies for 24h after the customer's last message"
              >
                {convDetail?.sessionOpen
                  ? `Session open · ${fmtTime(convDetail?.sessionMsRemaining)} left`
                  : "Session closed — template only"}
              </div>
            </div>
            <div className="wa-inbox__messages">
              {messages.map((m) => {
                const hasMedia = Boolean(messageMediaInfo(m));
                return (
                <div
                  key={m.id}
                  className={`wa-bubble ${m.direction === "outbound" ? "wa-bubble--out" : "wa-bubble--in"}${hasMedia ? " wa-bubble--media" : ""}`}
                >
                  <WaMessageMedia message={m} appId={appId} phoneNumberId={phoneNumberId} />
                  {(m.messageType === "template" || m.templateName) && (
                    <div className="wa-bubble__tpl-label">
                      Template · {m.templateName || displayMessageText(m)}
                    </div>
                  )}
                  <div className="wa-bubble__text">{renderWhatsAppMarkdown(displayMessageText(m))}</div>
                  <div className="wa-bubble__footer">
                    {fmtClock(m.providerTimestamp || m.createdAt)} {ticks(m)}
                  </div>
                </div>
                );
              })}
              <div ref={endRef} />
            </div>
            <div className="wa-inbox__composer">
              {!can("whatsapp.inbox.manage") ? (
                <div className="wa-muted small p-2">You do not have permission to reply in this inbox.</div>
              ) : (
                <div className="wa-inbox__composer-box">
                  <div className="wa-inbox__composer-top">
                    <select
                      className="form-select form-select-sm wa-inbox__tpl-select"
                      value={tplKey}
                      onChange={(e) => {
                        setTplKey(e.target.value);
                        setTplBodyParams("");
                        setTplButtonParam("");
                        setTplCopyCodeParam("");
                        setTplHeaderImageUrl("");
                        setTplHeaderImageFile(null);
                        setTplHeaderLat("");
                        setTplHeaderLng("");
                      }}
                      title="Optional when session is open; required when closed"
                    >
                      <option value="">
                        {convDetail?.sessionOpen ? "No template (free text)" : "Select template…"}
                      </option>
                      {approvedTemplates.map((t) => (
                        <option key={tplOptionKey(t)} value={tplOptionKey(t)}>
                          {t.name} · {t.language}
                        </option>
                      ))}
                    </select>
                    {selectedTpl && selectedTplVarCount > 0 ? (
                      <input
                        className="form-control form-control-sm font-monospace wa-inbox__tpl-vars"
                        placeholder={`Vars {{1}}…{{${selectedTplVarCount}}}`}
                        value={tplBodyParams}
                        onChange={(e) => setTplBodyParams(e.target.value)}
                      />
                    ) : null}
                    {selectedTpl && templateHasDynamicUrlButton(selectedTpl) ? (
                      <input
                        className="form-control form-control-sm font-monospace"
                        placeholder="Button URL {{1}}"
                        value={tplButtonParam}
                        onChange={(e) => setTplButtonParam(e.target.value)}
                      />
                    ) : null}
                    {selectedTpl && templateHasCopyCodeButton(selectedTpl) ? (
                      <input
                        className="form-control form-control-sm font-monospace"
                        placeholder="Copy offer code"
                        value={tplCopyCodeParam}
                        onChange={(e) => setTplCopyCodeParam(e.target.value)}
                      />
                    ) : null}
                    {selectedTpl && isFileHeaderFormat(templateHeaderFormat(selectedTpl)) ? (
                      <>
                        <input
                          type="file"
                          className="form-control form-control-sm wa-inbox__tpl-header-file"
                          accept={headerFileAccept(templateHeaderFormat(selectedTpl))}
                          title={`Header ${String(templateHeaderFormat(selectedTpl)).toLowerCase()} (required)`}
                          onChange={(e) =>
                            setTplHeaderImageFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)
                          }
                        />
                        <input
                          className="form-control form-control-sm wa-inbox__tpl-header-url"
                          placeholder="Header HTTPS URL"
                          value={tplHeaderImageUrl}
                          onChange={(e) => setTplHeaderImageUrl(e.target.value)}
                        />
                      </>
                    ) : selectedTpl && templateHeaderFormat(selectedTpl) === "LOCATION" ? (
                      <>
                        <input
                          className="form-control form-control-sm wa-inbox__tpl-header-coord"
                          placeholder="Lat"
                          value={tplHeaderLat}
                          onChange={(e) => setTplHeaderLat(e.target.value)}
                        />
                        <input
                          className="form-control form-control-sm wa-inbox__tpl-header-coord"
                          placeholder="Lng"
                          value={tplHeaderLng}
                          onChange={(e) => setTplHeaderLng(e.target.value)}
                        />
                      </>
                    ) : null}
                  </div>

                  <div className="wa-inbox__composer-main">
                    {selectedTpl ? (
                      <textarea
                        className="form-control form-control-sm wa-composer-input wa-composer-input--preview"
                        rows={2}
                        readOnly
                        value={
                          selectedTplPreview ||
                          "(No body text for this template — e.g. AUTH/OTP)"
                        }
                      />
                    ) : (
                      <textarea
                        className="form-control form-control-sm wa-composer-input"
                        rows={2}
                        placeholder={
                          convDetail?.sessionOpen
                            ? "Type your message…"
                            : "Select a template above — free text is locked until the customer messages you"
                        }
                        value={text}
                        disabled={!convDetail?.sessionOpen || sending}
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            if (!sending && convDetail?.sessionOpen && text.trim()) sendCustom();
                          }
                        }}
                      />
                    )}
                    <button
                      type="button"
                      className="btn btn-sm btn-primary wa-send-btn"
                      disabled={
                        sending ||
                        (selectedTpl
                          ? selectedTplVarCount > 0 &&
                            String(tplBodyParams || "")
                              .split(",")
                              .map((s) => s.trim())
                              .filter(Boolean).length !== selectedTplVarCount
                          : !convDetail?.sessionOpen || !text.trim())
                      }
                      onClick={() => (selectedTpl ? send() : sendCustom())}
                      title={selectedTpl ? "Send template" : "Send message"}
                    >
                      {sending ? "…" : "➤"}
                    </button>
                  </div>

                  <div
                    className={`wa-inbox__session-bar ${
                      convDetail?.sessionOpen ? "wa-inbox__session-bar--open" : "wa-inbox__session-bar--closed"
                    }`}
                  >
                    {convDetail?.sessionOpen ? (
                      <>
                        <span className="wa-inbox__session-dot" />
                        24h session open
                        {convDetail?.sessionMsRemaining != null
                          ? ` · ${fmtTime(convDetail.sessionMsRemaining)} left for free-text replies`
                          : ""}
                      </>
                    ) : (
                      <>
                        <span className="wa-inbox__session-dot" />
                        24h session closed — free text locked; use a template until the customer messages you
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ContactsPanel({ api, appId }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageSize = getDefaultPageSize();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [filterAccordionOpen, setFilterAccordionOpen] = useState(false);
  const tableScrollRef = useRef(null);
  const tableInnerRef = useRef(null);
  const tableMaxHeight = useTableMaxHeight(tableScrollRef);
  const isAtScrollEnd = useScrollEndDetection(tableScrollRef, tableInnerRef, rows.length);
  const [currentPage, setCurrentPage] = useState(() =>
    Math.max(1, parseInt(searchParams.get("page") || "1", 10))
  );
  const [draftFilters, setDraftFilters] = useState(() => ({
    search: searchParams.get("q") || "",
  }));
  const [appliedFilters, setAppliedFilters] = useState(() => ({ ...draftFilters }));

  useEffect(() => {
    const hasContent =
      filtersObjectHasAnyValue(draftFilters) || filtersObjectHasAnyValue(appliedFilters);
    if (!hasContent) {
      setFilterAccordionOpen(false);
      return;
    }
    setFilterAccordionOpen(true);
  }, [draftFilters, appliedFilters]);

  const writeUrl = (page, filters) => {
    const next = new URLSearchParams();
    if (page > 1) next.set("page", String(page));
    if (filters.search) next.set("q", filters.search);
    setSearchParams(next, { replace: true });
  };

  const load = useCallback(() => {
      setLoading(true);
    api("application/whatsapp/contacts/list", {
      page: currentPage,
      pageSize,
      search: appliedFilters.search || "",
    })
        .then(async (res) => {
          const result = await res.json().catch(() => ({}));
          if (result.status) {
            setRows(result.data || []);
            setTotal(result.pagination?.total ?? (result.data || []).length);
          }
        })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [api, currentPage, pageSize, appliedFilters]);

  useEffect(() => {
    load();
  }, [load]);

  const handleApplyFilters = () => {
    setAppliedFilters({ ...draftFilters });
    setCurrentPage(1);
    writeUrl(1, draftFilters);
  };

  const handleResetFilters = () => {
    const cleared = { search: "" };
    setDraftFilters(cleared);
    setAppliedFilters(cleared);
    setCurrentPage(1);
    writeUrl(1, cleared);
  };

  const totalPages = getTotalPages(total, pageSize);
  const safePage = Math.min(Math.max(1, currentPage), Math.max(1, totalPages));

  const goPage = (p) => {
    const next = Math.min(Math.max(1, p), Math.max(1, totalPages));
    setCurrentPage(next);
    writeUrl(next, appliedFilters);
  };

  return (
    <>
      <div className="list-page-filter-card">
        <button
          type="button"
          className="list-page-filter-accordion-header"
          onClick={() => setFilterAccordionOpen((o) => !o)}
          aria-expanded={filterAccordionOpen}
        >
          <span className="list-page-filter-accordion-title">Filter</span>
          <MdKeyboardArrowDown
            className={`list-page-filter-accordion-chevron ${filterAccordionOpen ? "list-page-filter-accordion-chevron--open" : ""}`}
            size={22}
            aria-hidden
          />
        </button>
        <Collapse in={filterAccordionOpen}>
          <div className="list-page-filter-accordion-body" role="region">
            <div className="list-page-filter-bar">
              <div className="list-page-filter-fields row g-3 align-items-end">
                <div className="list-page-filter-field col-12 col-lg-6">
          <input
                    type="text"
            className="form-control"
                    placeholder="Search name, phone, email, or Notify Sphere ID"
                    value={draftFilters.search}
                    onChange={(e) => setDraftFilters((d) => ({ ...d, search: e.target.value }))}
            onKeyDown={(e) => {
                      if (e.key === "Enter") handleApplyFilters();
            }}
          />
        </div>
                <div className="list-page-filter-field list-page-filter-actions col-12 col-lg-4">
                  <div className="add_app">
                    <button
                      type="button"
                      className="add_app_btn btn_theme_primary"
                      title="Apply filters"
                      onClick={handleApplyFilters}
                      disabled={loading}
                    >
                      <MdSearch />
                      <span>Search</span>
        </button>
          <button
            type="button"
                      className="add_app_btn_secondary btn_theme_secondary"
                      title="Reset filters"
                      onClick={handleResetFilters}
                    >
                      <MdRefresh />
                      <span>Reset</span>
          </button>
      </div>
                </div>
              </div>
            </div>
          </div>
        </Collapse>
      </div>

      <div className="dashboard_app_table dashboard_app_table--list-records">
        <div className="dashboard_table">
          <div ref={tableScrollRef} className="table_scroll_wrapper">
            <div
              ref={tableInnerRef}
              className={`table_inner table_y_scroll ${isAtScrollEnd ? "at-scroll-end" : ""}`}
              style={{
                maxHeight: tableMaxHeight ? `${tableMaxHeight}px` : "calc(100vh - 320px)",
                overflowY: "auto",
                overflowX: "auto",
              }}
            >
              <table className="table_outer table-sm">
          <thead>
            <tr>
              <th>Name</th>
              <th>Phone</th>
              <th>Notify Sphere ID</th>
              <th>Email</th>
              <th>Location</th>
              <th>Last seen</th>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                      <td colSpan={7} className="text-center text-muted">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                      <td colSpan={7} className="text-center text-muted">
                        No contacts found. Contacts come from app users with a phone, and from
                        customers who message your WhatsApp number.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.name ? r.name : <span className="text-muted">—</span>}</td>
                  <td className="font-monospace">{r.phoneE164}</td>
                  <td
                    className="small text-muted text-truncate"
                    style={{ maxWidth: 200 }}
                    title={r.notify_sphere_id || ""}
                  >
                    {r.notify_sphere_id && r.appUserId ? (
                      <button
                        type="button"
                        className="btn btn-link p-0 small text-truncate text-start"
                        style={{ maxWidth: "100%" }}
                        onClick={() => navigate(`/apps/${appId}/users/${r.appUserId}`)}
                      >
                        {r.notify_sphere_id}
                      </button>
                    ) : (
                      r.notify_sphere_id || "—"
                    )}
                  </td>
                  <td className="small">{r.email || "—"}</td>
                  <td className="small">{[r.city, r.state].filter(Boolean).join(", ") || "—"}</td>
                        <td className="small text-nowrap">{formatDateTime(r.lastInteractionAt) || "—"}</td>
                  <td>
                    <span className={`badge ${r.source === "whatsapp" ? "bg-success" : "bg-secondary"}`}>
                      {r.source === "whatsapp" ? "WhatsApp" : "App user"}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
          <ListPaginationBar
            page={safePage}
            setPage={goPage}
            total={total}
            pageSize={pageSize}
            totalPages={totalPages}
            ariaLabel="WhatsApp contacts pagination"
            loading={loading}
          />
          </div>
        </div>
      </div>
    </>
  );
}

function CampaignsPanel({ api, appId }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterAccordionOpen, setFilterAccordionOpen] = useState(false);
  const tableScrollRef = useRef(null);
  const tableInnerRef = useRef(null);
  const pageSize = getDefaultPageSize();
  const {
    openActionItem,
    dropdownPosition,
    dropdownRef,
    handleDropdownAction,
    handleOpenDropdown,
  } = useActionDropdown({ tableScrollRef, optionCount: 1, menuWidth: 200 });
  const tableMaxHeight = useTableMaxHeight(tableScrollRef);
  const [currentPage, setCurrentPage] = useState(() =>
    Math.max(1, parseInt(searchParams.get("page") || "1", 10))
  );
  const [draftFilters, setDraftFilters] = useState(() => ({
    sendId: searchParams.get("sendId") || "",
    status: searchParams.get("status") || "",
    dateFrom: searchParams.get("date_from") || "",
    dateTo: searchParams.get("date_to") || "",
    datePreset: searchParams.get("date_preset") || "",
  }));
  const [appliedFilters, setAppliedFilters] = useState(() => ({ ...draftFilters }));
  const isAtScrollEnd = useScrollEndDetection(tableScrollRef, tableInnerRef, rows.length);

  const statusSelectValue = useMemo(() => {
    const v = draftFilters.status;
    if (v === "" || v === null || v === undefined) return null;
    return (
      findOptionByValue(WHATSAPP_CAMPAIGN_STATUS, Number(v)) || {
        value: v,
        label: String(v),
      }
    );
  }, [draftFilters.status]);

  useEffect(() => {
    const hasContent =
      filtersObjectHasAnyValue(draftFilters) || filtersObjectHasAnyValue(appliedFilters);
    if (!hasContent) {
      setFilterAccordionOpen(false);
      return;
    }
    setFilterAccordionOpen(true);
  }, [draftFilters, appliedFilters]);

  // Legacy ?report= → dedicated detail page
  useEffect(() => {
    const raw = searchParams.get("report");
    const id = raw != null ? Number(raw) : null;
    if (id && Number.isFinite(id)) {
      navigate(`/apps/${appId}/whatsapp/campaigns/${id}/report`, { replace: true });
    }
  }, [searchParams, appId, navigate]);

  const load = useCallback(() => {
    setLoading(true);
    const statusNum =
      appliedFilters.status !== "" && appliedFilters.status != null
        ? Number(appliedFilters.status)
        : NaN;
    api("application/whatsapp/campaigns/list", {
      page: currentPage,
      pageSize,
      ...(findOptionByValue(WHATSAPP_CAMPAIGN_STATUS, statusNum)
        ? { status: statusNum }
        : {}),
      ...(appliedFilters.sendId.trim()
        ? { send_id: appliedFilters.sendId.trim(), q: appliedFilters.sendId.trim() }
        : {}),
      ...(appliedFilters.dateFrom && appliedFilters.dateTo
        ? { date_from: appliedFilters.dateFrom, date_to: appliedFilters.dateTo }
        : {}),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        if (result.status) {
          setRows(result.data || []);
          setTotalRows(Number(result.total) || 0);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [api, currentPage, pageSize, appliedFilters]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!rows.some((r) => Number(r.status) === WHATSAPP_CAMPAIGN_STATUS[2].value)) return undefined;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [rows, load]);

  const writeListUrl = (page, filters) => {
    const next = new URLSearchParams();
    if (page > 1) next.set("page", String(page));
    if (filters.sendId) next.set("sendId", filters.sendId);
    if (filters.status !== "" && filters.status != null) next.set("status", String(filters.status));
    if (filters.dateFrom) next.set("date_from", filters.dateFrom);
    if (filters.dateTo) next.set("date_to", filters.dateTo);
    if (filters.datePreset) next.set("date_preset", filters.datePreset);
    setSearchParams(next, { replace: true });
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...draftFilters });
    setCurrentPage(1);
    writeListUrl(1, draftFilters);
  };

  const handleResetFilters = () => {
    const cleared = { sendId: "", status: "", dateFrom: "", dateTo: "", datePreset: "" };
    setDraftFilters(cleared);
    setAppliedFilters(cleared);
    setCurrentPage(1);
    writeListUrl(1, cleared);
  };

  const openReport = (id) => {
    navigate(`/apps/${appId}/whatsapp/campaigns/${id}/report`);
  };

  const totalPages = getTotalPages(totalRows, pageSize);
  const safePage = Math.min(Math.max(1, currentPage), Math.max(1, totalPages));

  const goPage = (p) => {
    const next = Math.min(Math.max(1, p), Math.max(1, totalPages));
    setCurrentPage(next);
    writeListUrl(next, appliedFilters);
  };

  return (
    <>
      <div className="list-page-filter-card">
        <button
          type="button"
          className="list-page-filter-accordion-header"
          onClick={() => setFilterAccordionOpen((o) => !o)}
          aria-expanded={filterAccordionOpen}
        >
          <span className="list-page-filter-accordion-title">Filter</span>
          <MdKeyboardArrowDown
            className={`list-page-filter-accordion-chevron ${filterAccordionOpen ? "list-page-filter-accordion-chevron--open" : ""}`}
            size={22}
            aria-hidden
          />
        </button>
        <Collapse in={filterAccordionOpen}>
          <div className="list-page-filter-accordion-body" role="region">
            <div className="list-page-filter-bar">
              <div className="list-page-filter-fields row g-3 align-items-end">
                <div className="list-page-filter-field col-12 col-lg-4">
            <input
                    type="text"
              className="form-control"
                    placeholder="Send ID / name / template"
                    value={draftFilters.sendId}
                    onChange={(e) => setDraftFilters((d) => ({ ...d, sendId: e.target.value }))}
            />
          </div>
                <div className="list-page-filter-field col-12 col-lg-4">
                  <div style={{ minWidth: 0, width: "100%" }}>
                    <Select
                      inputId="wa-campaigns-status"
                      instanceId="wa-campaigns-status"
                      classNamePrefix="ns-wa-campaigns-status"
                      isSearchable
                      isClearable
                      placeholder="Status"
                      aria-label="Status"
                      options={WHATSAPP_CAMPAIGN_STATUS}
                      value={statusSelectValue}
                      onChange={(opt) =>
                        setDraftFilters((d) => ({
                          ...d,
                          status: opt?.value != null ? opt.value : "",
                        }))
                      }
                      styles={selectStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                    />
          </div>
                </div>
                <div className="list-page-filter-field col-12 col-lg-4">
                  <DateRangePicker
                    dateFrom={draftFilters.dateFrom || ""}
                    dateTo={draftFilters.dateTo || ""}
                    preset={draftFilters.datePreset || ""}
                    onChange={({ dateFrom, dateTo, preset }) =>
                      setDraftFilters((d) => ({
                        ...d,
                        dateFrom: dateFrom || "",
                        dateTo: dateTo || "",
                        datePreset: preset || "",
                      }))
                    }
                    label=""
                    placeholder="Sent date"
                    allowClear
                  />
                </div>
                <div className="list-page-filter-field list-page-filter-actions col-12 col-lg-4">
                  <div className="add_app">
                    <button
                      type="button"
                      className="add_app_btn btn_theme_primary"
                      title="Apply filters"
                      onClick={handleApplyFilters}
                    >
                      <MdSearch />
                      <span>Search</span>
                    </button>
                    <button
                      type="button"
                      className="add_app_btn_secondary btn_theme_secondary"
                      title="Reset filters"
                      onClick={handleResetFilters}
                    >
                      <MdRefresh />
                      <span>Reset</span>
            </button>
          </div>
        </div>
      </div>
            </div>
          </div>
        </Collapse>
      </div>

      <div className="dashboard_app_table dashboard_app_table--list-records">
        <div className="dashboard_table">
          <div className="table_scroll_wrapper" ref={tableScrollRef}>
            <div
              ref={tableInnerRef}
              className={`table_inner table_y_scroll ${isAtScrollEnd ? "at-scroll-end" : ""}`}
              style={{
                maxHeight: tableMaxHeight ? `${tableMaxHeight}px` : "calc(100vh - 320px)",
                overflowY: "auto",
                overflowX: "auto",
              }}
            >
              <table className="table_outer table-sm">
          <thead>
            <tr>
                    <th>Send ID</th>
              <th>Name</th>
                    <th>Template</th>
                    <th>Audience</th>
              <th>Status</th>
                    <th>Sent At</th>
              <th>Sent</th>
                    <th>Delivered</th>
                    <th>Read</th>
                    <th>Failed</th>
                    <th className="col-action" style={{ textAlign: "right" }}></th>
            </tr>
          </thead>
          <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={11} className="text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : !rows.length ? (
                    <tr>
                      <td colSpan={11} className="text-center text-muted">
                        No campaigns found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r) => {
                      const sendId = r.payload?.sendId || String(r.id);
                      const tpl = r.payload?.templateName || r.payload?.template_name || "—";
                      const statusOpt = findOptionByValue(WHATSAPP_CAMPAIGN_STATUS, r.status);
                      const aud = r.payload?.bulkAudience;
                      const phoneCount = Array.isArray(r.payload?.phones)
                        ? r.payload.phones.length
                        : null;
                      let audienceLabel = "—";
                      if (aud?.audienceType === "segment") {
                        const n = Array.isArray(aud.segmentIds) ? aud.segmentIds.length : 0;
                        audienceLabel = n ? `Segment (${n})` : "Segment";
                      } else if (aud?.audienceType === "subscribed") audienceLabel = "All subscribed";
                      else if (aud?.audienceType === "test") audienceLabel = "Test users";
                      else if (phoneCount != null) audienceLabel = `${phoneCount} number(s)`;
                      return (
              <tr key={r.id}>
                          <td className="small font-monospace text-break">{sendId}</td>
                <td>{r.name}</td>
                          <td className="small">{tpl}</td>
                          <td className="small">{audienceLabel}</td>
                          <td>
                            {statusOpt ? (
                              <span className={statusOpt.badgeClass}>{statusOpt.label}</span>
                            ) : (
                              r.status
                            )}
                          </td>
                          <td className="small text-nowrap">{formatDateTime(r.createdAt)}</td>
                          <td>{r.statsSent ?? 0}</td>
                          <td>{r.statsDelivered ?? 0}</td>
                          <td>{r.statsRead ?? 0}</td>
                          <td>{r.statsFailed ?? 0}</td>
                          <td className="col-action" style={{ textAlign: "right" }}>
                            <button
                              type="button"
                              className="dropdown-trigger-btn btn btn-link p-0"
                              aria-label="Actions"
                              onClick={(e) => handleOpenDropdown(e, r.id, r)}
                            >
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                <circle cx="8" cy="3" r="1.5" />
                                <circle cx="8" cy="8" r="1.5" />
                                <circle cx="8" cy="13" r="1.5" />
                              </svg>
                    </button>
                </td>
              </tr>
                      );
                    })
                  )}
          </tbody>
        </table>
      </div>
          <ListPaginationBar
            page={safePage}
            setPage={goPage}
            total={totalRows}
            pageSize={pageSize}
            totalPages={totalPages}
            ariaLabel="WhatsApp campaigns pagination"
            loading={loading}
          />
          </div>
        </div>
      </div>
      {openActionItem &&
        dropdownPosition &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: "fixed",
              left: dropdownPosition.left,
              top: dropdownPosition.top,
              zIndex: 10000,
              minWidth: 200,
            }}
          >
            <div className="dropdown-menu-subscription">
              <button
                type="button"
                onClick={() =>
                  handleDropdownAction(() => openReport(openActionItem.id))
                }
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  textAlign: "left",
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                  style={{ marginRight: 8, flexShrink: 0 }}
                >
                  <path d="M4 0h8a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V2a2 2 0 012-2zm0 1a1 1 0 00-1 1v12a1 1 0 001 1h8a1 1 0 001-1V2a1 1 0 00-1-1H4z" />
                  <path d="M4 4h8v1H4V4zm0 3h8v1H4V7zm0 3h5v1H4v-1z" />
                </svg>
                <span style={{ fontSize: "0.875rem" }}>View Report</span>
              </button>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}


function AnalyticsPanel({ api }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api("application/whatsapp/analytics/summary", { days: 14 })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        if (result.status) setData(result.data);
      })
      .catch(() => {});
  }, [api]);

  const chartData = useMemo(() => {
    if (!data?.statusBreakdown) return [];
    const sb = data.statusBreakdown;
    return WHATSAPP_MESSAGE_STATUS.map((opt) => ({
      name: opt.label,
      v: sb[opt.key] || 0,
    }));
  }, [data]);

  if (!data) return <div className="wa-muted">Loading…</div>;

  return (
    <div className="wa-analytics">
      <div className="row g-3">
        <div className="col-md-3">
          <div className="card shadow-sm p-3 wa-kpi">
            <div className="wa-muted small">Messages sent</div>
            <div className="wa-kpi__val">{data.messagesSent}</div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card shadow-sm p-3 wa-kpi">
            <div className="wa-muted small">Delivery rate</div>
            <div className="wa-kpi__val">{data.deliveryRatePct}%</div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card shadow-sm p-3 wa-kpi">
            <div className="wa-muted small">Read rate</div>
            <div className="wa-kpi__val">{data.readRatePct}%</div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card shadow-sm p-3 wa-kpi">
            <div className="wa-muted small">Conversations</div>
            <div className="wa-kpi__val">{data.activeConversations}</div>
          </div>
        </div>
      </div>
      <div className="card shadow-sm p-3 mt-3 wa-card" style={{ height: 320 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData}>
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="v" fill="#25D366" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const WA_VERTICALS = [
  ["", "— Select category —"],
  ["OTHER", "Other"],
  ["AUTO", "Automotive"],
  ["BEAUTY", "Beauty, spa & salon"],
  ["APPAREL", "Clothing & apparel"],
  ["EDU", "Education"],
  ["ENTERTAIN", "Entertainment"],
  ["EVENT_PLAN", "Event planning & service"],
  ["FINANCE", "Finance & banking"],
  ["GROCERY", "Food & grocery"],
  ["GOVT", "Public service"],
  ["HOTEL", "Hotel & lodging"],
  ["HEALTH", "Medical & health"],
  ["NONPROFIT", "Non-profit"],
  ["PROF_SERVICES", "Professional services"],
  ["RETAIL", "Shopping & retail"],
  ["TRAVEL", "Travel & transportation"],
  ["RESTAURANT", "Restaurant"],
];

function BusinessPanel({ api, phoneNumberId, displayPhone, onSaved }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [about, setAbout] = useState("");
  const [desc, setDesc] = useState("");
  const [address, setAddress] = useState("");
  const [email, setEmail] = useState("");
  const [website1, setWebsite1] = useState("");
  const [website2, setWebsite2] = useState("");
  const [vertical, setVertical] = useState("");

  const load = useCallback(() => {
    if (!phoneNumberId) return;
    setLoading(true);
    api("application/whatsapp/business/profile", { phone_number_id: phoneNumberId })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        setLoading(false);
        if (result.status && result.data) {
          const p = result.data;
          setProfile(p);
          setAbout(p.about || "");
          setDesc(p.description || "");
          setAddress(p.address || "");
          setEmail(p.email || "");
          const sites = Array.isArray(p.websites) ? p.websites : [];
          setWebsite1(sites[0] || "");
          setWebsite2(sites[1] || "");
          setVertical(p.vertical && p.vertical !== "UNDEFINED" ? p.vertical : "");
        } else if (!result.status) {
          toast.error(result.message || "Could not load profile");
        }
      })
      .catch(() => setLoading(false));
  }, [api, phoneNumberId]);

  useEffect(() => {
    load();
  }, [load]);

  const save = () => {
    setSaving(true);
    api("application/whatsapp/business/profile/update", {
      phone_number_id: phoneNumberId,
      about,
      description: desc,
      address,
      email,
      vertical,
      websites: [website1, website2],
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        setSaving(false);
        if (result.status) {
          toast.success("Profile updated on WhatsApp");
          load();
          if (typeof onSaved === "function") onSaved();
        } else {
          toast.error(result.message || "Error");
        }
      })
      .catch(() => {
        setSaving(false);
        toast.error("Network error");
      });
  };

  return (
    <div>
      <p className="small text-muted mb-3">
        This is what customers see when they open your business profile in WhatsApp
        {displayPhone ? (
          <>
            {" "}
            for <strong>{displayPhone}</strong>
          </>
        ) : null}
        .
      </p>
      {loading ? <p className="text-muted small">Loading…</p> : null}
      <div className="d-flex gap-3 align-items-start mb-3">
        {profile?.profile_picture_url ? (
          <img
            src={profile.profile_picture_url}
            alt="Profile"
            className="rounded-circle"
            style={{ width: 64, height: 64, objectFit: "cover" }}
          />
        ) : (
          <div
            className="rounded-circle bg-light d-flex align-items-center justify-content-center text-muted"
            style={{ width: 64, height: 64, fontSize: 24 }}
          >
            🏢
          </div>
        )}
        <div className="small text-muted align-self-center">
          Profile photo can only be changed in Meta&apos;s WhatsApp Manager (API needs a special
          upload flow).
        </div>
      </div>
      <div className="row g-3">
        <div className="col-md-6">
          <label className="form-label">About (short status line)</label>
          <input
            className="form-control"
            maxLength={139}
            value={about}
            onChange={(e) => setAbout(e.target.value)}
            placeholder="e.g. Personal care that cares"
          />
          <div className="form-text">{about.length}/139</div>
        </div>
        <div className="col-md-6">
          <label className="form-label">Category</label>
          <select className="form-select" value={vertical} onChange={(e) => setVertical(e.target.value)}>
            {WA_VERTICALS.map(([v, label]) => (
              <option key={v || "none"} value={v}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-12">
          <label className="form-label">Description</label>
          <textarea
            className="form-control"
            rows={3}
            maxLength={512}
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="What your business does"
          />
          <div className="form-text">{desc.length}/512</div>
        </div>
        <div className="col-md-6">
          <label className="form-label">Address</label>
          <input
            className="form-control"
            maxLength={256}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Street, city, state"
          />
        </div>
        <div className="col-md-6">
          <label className="form-label">Email</label>
          <input
            type="email"
            className="form-control"
            maxLength={128}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="support@yourbusiness.com"
          />
        </div>
        <div className="col-md-6">
          <label className="form-label">Website</label>
          <input
            className="form-control"
            maxLength={256}
            value={website1}
            onChange={(e) => setWebsite1(e.target.value)}
            placeholder="https://yourbusiness.com"
          />
        </div>
        <div className="col-md-6">
          <label className="form-label">Website 2 (optional)</label>
          <input
            className="form-control"
            maxLength={256}
            value={website2}
            onChange={(e) => setWebsite2(e.target.value)}
            placeholder="https://shop.yourbusiness.com"
          />
        </div>
      </div>
      <div className="mt-3">
        {can("whatsapp.business.manage") ? (
        <button type="button" className="btn btn-primary" onClick={save} disabled={saving || loading || !phoneNumberId}>
          {saving ? "Saving…" : "Save profile"}
        </button>
        ) : null}
      </div>
    </div>
  );
}

function PhonesPanel({ api }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const pageSize = getDefaultPageSize();
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState("");
  const [filterAccordionOpen, setFilterAccordionOpen] = useState(false);
  const [profilePhone, setProfilePhone] = useState(null);
  const tableScrollRef = useRef(null);
  const tableInnerRef = useRef(null);
  const {
    openActionItem,
    dropdownPosition,
    dropdownRef,
    handleDropdownAction,
    handleOpenDropdown,
  } = useActionDropdown({
    tableScrollRef,
    optionCount: (item) =>
      (waMeUrl(item?.display_phone_number) ? 1 : 0) +
      (can("whatsapp.business.view") ? 1 : 0),
    menuWidth: 220,
  });
  const tableMaxHeight = useTableMaxHeight(tableScrollRef);
  const [currentPage, setCurrentPage] = useState(() =>
    Math.max(1, parseInt(searchParams.get("page") || "1", 10))
  );
  const [draftFilters, setDraftFilters] = useState(() => ({
    search: searchParams.get("q") || "",
    usageType: searchParams.get("usage_type") || "",
  }));
  const [appliedFilters, setAppliedFilters] = useState(() => ({ ...draftFilters }));
  const isAtScrollEnd = useScrollEndDetection(tableScrollRef, tableInnerRef, allRows.length);

  useEffect(() => {
    const hasContent =
      filtersObjectHasAnyValue(draftFilters) || filtersObjectHasAnyValue(appliedFilters);
    if (!hasContent) {
      setFilterAccordionOpen(false);
      return;
    }
    setFilterAccordionOpen(true);
  }, [draftFilters, appliedFilters]);

  const usageTypeSelectValue = useMemo(() => {
    const v = draftFilters.usageType;
    if (v === "" || v === null || v === undefined) return null;
    return (
      findOptionByValue(WHATSAPP_USAGE_TYPE_CODES, Number(v)) || {
        value: v,
        label: String(v),
      }
    );
  }, [draftFilters.usageType]);

  const writeUrl = (page, filters) => {
    const next = new URLSearchParams();
    if (page > 1) next.set("page", String(page));
    if (filters.search) next.set("q", filters.search);
    if (filters.usageType !== "" && filters.usageType != null) {
      next.set("usage_type", String(filters.usageType));
    }
    setSearchParams(next, { replace: true });
  };

  const load = useCallback(() => {
    setLoading(true);
    api("application/whatsapp/business/phones", {})
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        if (result.status) {
          setAllRows(Array.isArray(result.data?.data) ? result.data.data : []);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredRows = useMemo(() => {
    const q = String(appliedFilters.search || "")
      .trim()
      .toLowerCase();
    const usage = appliedFilters.usageType;
    return allRows.filter((p) => {
      if (usage !== "" && usage != null) {
        if (Number(p.usage_type) !== Number(usage)) return false;
      }
      if (!q) return true;
      const hay = [
        p.display_phone_number,
        p.id,
        p.quality_rating,
        getWhatsAppQualityDisplay(p.quality_rating)?.label,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [allRows, appliedFilters]);

  const total = filteredRows.length;
  const totalPages = getTotalPages(total, pageSize);
  const safePage = Math.min(Math.max(1, currentPage), Math.max(1, totalPages));
  const startIdx = (safePage - 1) * pageSize;
  const endIdx = startIdx + pageSize;
  const pageRows = filteredRows.slice(startIdx, endIdx);

  useEffect(() => {
    if (currentPage > totalPages && totalPages >= 1) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleApplyFilters = () => {
    setAppliedFilters({ ...draftFilters });
    setCurrentPage(1);
    writeUrl(1, draftFilters);
  };

  const handleResetFilters = () => {
    const cleared = { search: "", usageType: "" };
    setDraftFilters(cleared);
    setAppliedFilters(cleared);
    setCurrentPage(1);
    writeUrl(1, cleared);
  };

  const goPage = (p) => {
    const next = Math.min(Math.max(1, p), Math.max(1, totalPages));
    setCurrentPage(next);
    writeUrl(next, appliedFilters);
  };

  const assignUsageType = (phoneNumberId, usageType) => {
    setSavingId(String(phoneNumberId));
    api("application/whatsapp/business/phones/usage-type", {
      phone_number_id: phoneNumberId,
      usage_type: usageType === "" ? null : Number(usageType),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        if (result.status) {
          toast.success("Usage type saved");
          load();
        } else toast.error(result.message || "Could not save usage type");
      })
      .catch(() => toast.error("Could not save usage type"))
      .finally(() => setSavingId(""));
  };

  return (
    <>
      <div className="list-page-filter-card">
        <button
          type="button"
          className="list-page-filter-accordion-header"
          onClick={() => setFilterAccordionOpen((o) => !o)}
          aria-expanded={filterAccordionOpen}
        >
          <span className="list-page-filter-accordion-title">Filter</span>
          <MdKeyboardArrowDown
            className={`list-page-filter-accordion-chevron ${filterAccordionOpen ? "list-page-filter-accordion-chevron--open" : ""}`}
            size={22}
            aria-hidden
          />
      </button>
        <Collapse in={filterAccordionOpen}>
          <div className="list-page-filter-accordion-body" role="region">
            <div className="list-page-filter-bar">
              <div className="list-page-filter-fields row g-3 align-items-end">
                <div className="list-page-filter-field col-12 col-lg-4">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search display number, ID, quality"
                    value={draftFilters.search}
                    onChange={(e) => setDraftFilters((d) => ({ ...d, search: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleApplyFilters();
                    }}
                  />
                </div>
                <div className="list-page-filter-field col-12 col-lg-4">
                  <div style={{ minWidth: 0, width: "100%" }}>
                    <Select
                      inputId="wa-phones-usage"
                      instanceId="wa-phones-usage"
                      classNamePrefix="ns-wa-phones-usage"
                      isSearchable
                      isClearable
                      placeholder="Usage type"
                      aria-label="Usage type"
                      options={WHATSAPP_USAGE_TYPE_CODES}
                      value={usageTypeSelectValue}
                      onChange={(opt) =>
                        setDraftFilters((d) => ({
                          ...d,
                          usageType: opt?.value != null ? opt.value : "",
                        }))
                      }
                      styles={selectStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                    />
                  </div>
                </div>
                <div className="list-page-filter-field list-page-filter-actions col-12 col-lg-4">
                  <div className="add_app">
                    <button
                      type="button"
                      className="add_app_btn btn_theme_primary"
                      title="Apply filters"
                      onClick={handleApplyFilters}
                    >
                      <MdSearch />
                      <span>Search</span>
                    </button>
                    <button
                      type="button"
                      className="add_app_btn_secondary btn_theme_secondary"
                      title="Reset filters"
                      onClick={handleResetFilters}
                    >
                      <MdRefresh />
                      <span>Reset</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Collapse>
      </div>

      <div className="dashboard_app_table dashboard_app_table--list-records">
        <div className="dashboard_table">
          <div className="table_scroll_wrapper" ref={tableScrollRef}>
            <div
              ref={tableInnerRef}
              className={`table_inner table_y_scroll ${isAtScrollEnd ? "at-scroll-end" : ""}`}
              style={{
                maxHeight: tableMaxHeight ? `${tableMaxHeight}px` : "calc(100vh - 320px)",
                overflowY: "auto",
                overflowX: "auto",
              }}
            >
              <table className="table_outer table-sm">
          <thead>
            <tr>
              <th>Display</th>
              <th>ID</th>
              <th>Quality</th>
              <th>Usage type</th>
                    <th className="col-action" style={{ textAlign: "right" }}></th>
            </tr>
          </thead>
          <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={5} className="text-center text-muted">
                        Loading…
                      </td>
                    </tr>
                  ) : !pageRows.length ? (
                    <tr>
                      <td colSpan={5} className="text-center text-muted">
                        No phone numbers found
                      </td>
                    </tr>
                  ) : (
                    pageRows.map((p) => {
              const qualityDisplay = getWhatsAppQualityDisplay(p.quality_rating);
              return (
              <tr key={p.id}>
                <td>{p.display_phone_number}</td>
                <td className="small text-muted">{p.id}</td>
                <td>
                  <span className={qualityDisplay.badgeClass}>{qualityDisplay.label}</span>
                </td>
                <td>
                  <select
                    className="form-select form-select-sm wa-usage-type"
                    value={p.usage_type == null ? "" : String(p.usage_type)}
                    disabled={savingId === String(p.id)}
                    onChange={(e) => assignUsageType(p.id, e.target.value)}
                  >
                    <option value="">Not set</option>
                    {WHATSAPP_USAGE_TYPE_CODES.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </td>
                        <td className="col-action" style={{ textAlign: "right" }}>
                          <button
                            type="button"
                            className="dropdown-trigger-btn btn btn-link p-0"
                            aria-label="Actions"
                            onClick={(e) => handleOpenDropdown(e, p.id, p)}
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                              <circle cx="8" cy="3" r="1.5" />
                              <circle cx="8" cy="8" r="1.5" />
                              <circle cx="8" cy="13" r="1.5" />
                            </svg>
                          </button>
                </td>
              </tr>
                    );
                    })
                  )}
                </tbody>
              </table>
            </div>
          <ListPaginationBar
            page={safePage}
            setPage={goPage}
            total={total}
            pageSize={pageSize}
            totalPages={totalPages}
            ariaLabel="WhatsApp phones pagination"
            loading={loading}
          />
          </div>
        </div>
      </div>
      {openActionItem &&
        dropdownPosition &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: "fixed",
              left: dropdownPosition.left,
              top: dropdownPosition.top,
              zIndex: 10000,
              minWidth: 220,
            }}
          >
            <div className="dropdown-menu-subscription">
              {waMeUrl(openActionItem.display_phone_number) ? (
                <button
                  type="button"
                  onClick={() =>
                    handleDropdownAction(async () => {
                      const url = waMeUrl(openActionItem.display_phone_number);
                      if (!url) return;
                      await copyToClipboard(url);
                      toast.success("Copied");
                    })
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    textAlign: "left",
                  }}
                >
                  <MdContentCopy size={18} style={{ marginRight: 8, flexShrink: 0 }} />
                  <span style={{ fontSize: "0.875rem" }}>Copy message link</span>
                </button>
              ) : null}
              {can("whatsapp.business.view") ? (
                <button
                  type="button"
                  onClick={() =>
                    handleDropdownAction(() => setProfilePhone(openActionItem))
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    textAlign: "left",
                  }}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 16 16"
                    fill="currentColor"
                    style={{ marginRight: 8, flexShrink: 0 }}
                  >
                    <path d="M8 0a8 8 0 100 16A8 8 0 008 0zM4.5 7.5a.5.5 0 01.5-.5h6a.5.5 0 010 1H5a.5.5 0 01-.5-.5zm.5 2a.5.5 0 000 1h4a.5.5 0 000-1H5z" />
                  </svg>
                  <span style={{ fontSize: "0.875rem" }}>Business profile</span>
                </button>
              ) : null}
            </div>
          </div>,
          document.body
        )}
      {profilePhone ? (
        <Modal
          title="Business profile"
          maxWidth="720px"
          onClose={() => setProfilePhone(null)}
        >
          <BusinessPanel
            api={api}
            phoneNumberId={String(profilePhone.id)}
            displayPhone={profilePhone.display_phone_number || ""}
          />
        </Modal>
      ) : null}
    </>
  );
}

/**
 * WhatsApp campaign delivery detail — same shell as push MessageReport (header + KPIs + activity list).
 */
export function WhatsAppCampaignReport() {
  const { appId, campaignId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageSize = getDefaultPageSize();

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [appData, setAppData] = useState(null);

  const statusFilter = searchParams.get("status") || "";
  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10));

  useEffect(() => {
    if (!appId) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetchWithAuth(Utils.baseurl + "getbyid/application", {
          method: "POST",
          body: JSON.stringify({ id: appId }),
        });
        const result = await response.json();
        if (!cancelled && result.status) setAppData(result.data);
      } catch (_) {}
    })();
    return () => {
      cancelled = true;
    };
  }, [appId]);

  const loadReport = useCallback(() => {
    if (!appId || !campaignId) return;
    setLoading(true);
    const statusNum = statusFilter !== "" ? Number(statusFilter) : NaN;
    const offset = (page - 1) * pageSize;
    fetchWithAuth(Utils.baseurl + "application/whatsapp/campaigns/report", {
      method: "POST",
      body: JSON.stringify({
        app_id: appId,
        campaign_id: Number(campaignId),
        limit: pageSize,
        offset,
        ...(findOptionByValue(WHATSAPP_MESSAGE_STATUS, statusNum) ? { status: statusNum } : {}),
      }),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        if (result.status) setReport(result.data);
        else toast.error(result.message || "Could not load report");
      })
      .catch(() => toast.error("Could not load report"))
      .finally(() => setLoading(false));
  }, [appId, campaignId, statusFilter, page, pageSize]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  useEffect(() => {
    const campStatus = Number(report?.campaign?.status);
    if (campStatus !== WHATSAPP_CAMPAIGN_STATUS[2].value) return undefined;
    const t = setInterval(loadReport, 8000);
    return () => clearInterval(t);
  }, [report?.campaign?.status, loadReport]);

  const handleBack = () => navigate(`/apps/${appId}/whatsapp/campaigns`);

  const setStatusFilter = (value) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (value === "" || value == null) p.delete("status");
        else p.set("status", String(value));
        p.set("page", "1");
        return p;
      },
      { replace: true }
    );
  };

  const goPage = (p) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("page", String(p));
        return next;
      },
      { replace: true }
    );
  };

  if (!campaignId) {
    return (
      <div className="message-report-container">
        <div className="text-center p-5">Invalid report link.</div>
      </div>
    );
  }

  const camp = report?.campaign || {};
  const sc = report?.statusCounts || {};
  const messages = report?.messages || [];
  const total = Number(report?.total) || 0;
  const totalPages = getTotalPages(total, pageSize);
  const safePage = Math.min(Math.max(1, page), Math.max(1, totalPages));
  const statusOpt = findOptionByValue(WHATSAPP_CAMPAIGN_STATUS, camp.status);
  const tpl = camp.payload?.templateName || camp.payload?.template_name || "—";
  const sendId = camp.payload?.sendId || String(camp.id || campaignId);

  return (
    <div className="message-report-container wa-campaign-report">
      <div className="report-header">
        <div className="report-breadcrumb">
          <span className="breadcrumb-link" onClick={handleBack}>
            {appData?.organization?.oganization_name || "Organization"}
          </span>
          <span className="breadcrumb-separator">&gt;</span>
          <span className="breadcrumb-link" onClick={handleBack}>
            {appData?.application_name || "Application"}
          </span>
          <span className="breadcrumb-separator">&gt;</span>
          <span className="breadcrumb-link" onClick={handleBack}>
            WhatsApp
          </span>
          <span className="breadcrumb-separator">&gt;</span>
          <span className="breadcrumb-current">Campaigns</span>
        </div>
        <div className="report-actions">
          <div className="add_app">
            <button
              type="button"
              className="add_app_btn_secondary btn_theme_secondary"
              onClick={handleBack}
            >
              ← Back
            </button>
          </div>
        </div>
      </div>

      <div className="message-title-section">
        <h2 className="message-title">{camp.name || `Campaign #${campaignId}`}</h2>
        {statusOpt ? <span className={statusOpt.badgeClass}>{statusOpt.label}</span> : null}
      </div>
      <p className="message-meta">
        Template: {tpl}. Send ID: {sendId}. Started sending at:{" "}
        {formatDateTime(camp.createdAt, { seconds: true })}
      </p>

      <div className="row g-3 mb-4">
        {WHATSAPP_MESSAGE_STATUS.map((opt) => (
          <div className="col-6 col-md" key={opt.key}>
            <div className="stat-card">
              <div className="stat-label">{opt.label}</div>
              <div className="stat-value">{sc[opt.key] || 0}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="audience-activity">
        <div className="activity-header">
          <h4>Audience activity</h4>
          <div className="add_app">
            <button
              type="button"
              className="add_app_btn_secondary btn_theme_secondary"
              disabled={loading}
              onClick={loadReport}
            >
              {loading ? "Loading…" : "Refresh"}
            </button>
          </div>
        </div>

        <div className="activity-filters mb-3">
          <button
            type="button"
            className={`filter-chip ${statusFilter === "" ? "filter-chip--active" : ""}`}
            onClick={() => setStatusFilter("")}
          >
            All
          </button>
          {WHATSAPP_MESSAGE_STATUS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`filter-chip ${String(statusFilter) === String(opt.value) ? "filter-chip--active" : ""}`}
              onClick={() => setStatusFilter(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th>Phone</th>
                <th>Status</th>
                <th>Error</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {loading && !messages.length ? (
                <tr>
                  <td colSpan={4} className="text-center text-muted">
                    Loading…
                  </td>
                </tr>
              ) : messages.length ? (
                messages.map((m) => {
                const st = findOptionByValue(WHATSAPP_MESSAGE_STATUS, m.status);
                return (
                  <tr key={m.id}>
                    <td className="font-monospace">{m.toPhone}</td>
                    <td>{st ? <span className={st.badgeClass}>{st.label}</span> : m.status}</td>
                    <td className="small text-break">
                      {Number(m.status) === WHATSAPP_MESSAGE_STATUS[4].value
                        ? [m.errorCode, m.errorDetail].filter(Boolean).join(" — ") || "—"
                        : "—"}
                    </td>
                    <td className="small text-nowrap">
                      {m.updatedAt ? formatDateTime(m.updatedAt) : "—"}
                    </td>
                  </tr>
                );
              })
              ) : (
                <tr>
                  <td colSpan={4} className="text-muted text-center">
                    No recipient rows.
                  </td>
                </tr>
              )}
          </tbody>
        </table>
        </div>

        <ListPaginationBar
          page={safePage}
          setPage={goPage}
          total={total}
          pageSize={pageSize}
          totalPages={totalPages}
          ariaLabel="Campaign recipients pagination"
          loading={loading}
        />
      </div>
    </div>
  );
}

export default WhatsAppPlatform;
